// 会話エンジンの解決と、エンジンが満たす型 (spec 2026-08-29 D1 / D7、2026-09-14 L1 / L2)。
// 切り替えは URL パラメータ ?engine=realtime / ?engine=live のみ。未指定・未知値・非ブラウザは
// すべて cascade に fail-open する (誤入力で経路が死なない)。
// src/api/sttClientFactory.ts の resolveSttProvider と同じ流儀。

import type { Question } from "../survey/schema";

// 再 export しておく (呼び出し側が出所を 2 つ覚えなくて済む)
export { REALTIME_MODEL } from "./realtimeModel";

export type ConversationEngineKind = "cascade" | "realtime" | "live";

/**
 * 現在の URL から会話エンジンを解決する。
 * 呼び出しのたびに window.location.search を読む (module-top const にしない) —
 * ?stt=openai / ?scribe-debug=1 と同じ流儀で、テストが history.replaceState で
 * 差し替えられるようにするため。
 */
export function resolveEngine(): ConversationEngineKind {
  if (typeof window === "undefined") return "cascade";
  const v = new URLSearchParams(window.location.search).get("engine");
  return v === "realtime" || v === "live" ? v : "cascade";
}

/**
 * engine の見かけの状態。**既存の MicState と 1:1 で対応させる** —
 * MicState の "speaking" は「ユーザーが話している」なので、AI 側は "ai-speaking" と
 * 明示的に分ける (同じ語が逆の意味を持つと画面の配線が静かに入れ替わる)。
 *   idle → idle / listening → listening / user-speaking → speaking
 *   thinking → thinking / ai-speaking → ai-speaking
 */
export type EngineState = "idle" | "listening" | "user-speaking" | "thinking" | "ai-speaking";

/** 聞き直しの理由。realtime は tool の引数、cascade は verdict から来る。 */
export type NeedRepeatReason = "no-content" | "off-topic" | "unclear";

export type EngineError = {
  /** connection-failed は live の接続 (WebRTC)・セッションの作成・切断 */
  code: "token-failed" | "ws-failed" | "tool-invalid" | "timeout" | "connection-failed";
  message: string;
};

/** 回答の出どころ。**live だけが持つ** (spec 2026-09-14 §3.5 / L13)。 */
export type AnswerMeta = {
  /** live = live モデルが backend に任せた / app = アプリが文字起こしを直接渡した (二重の網) */
  via?: "live" | "app";
  /**
   * その設問の窓で聞いた入力の文字起こし。value (backend の記録) との差が
   * 「backend が書き換えたか」の一次データになる
   */
  heard?: string;
};

/**
 * tool の出どころ。**live だけが持つ** (氏名の二重の網、spec 2026-09-14 §9.4)。
 * どちらの経路で記録したかを残し、live モデルだけで何回通ったかを数える
 */
export type ToolMeta = Pick<AnswerMeta, "via">;

export type EngineCallbacks = {
  /**
   * record_answer / need_repeat 以外の tool が呼ばれた。事前チェックの
   * confirm_name などを呼び出し側が扱うための素通し口 (引数は parse 済み)。meta は live だけが持つ。
   */
  onToolCall?: (
    name: string,
    args: Record<string, unknown>,
    callId: string,
    meta?: ToolMeta
  ) => void;
  /** 回答が確定した。value は要約せず本人の言葉のまま。meta は live だけが持つ。 */
  onAnswerCommitted: (questionId: string, value: string, meta?: AnswerMeta) => void;
  /**
   * 答えになっていないので聞き直す。回数を数えるのは呼び出し側 —
   * 原則 5 の到達性 (テキスト入力への逃げ道) を engine に依存させない。
   */
  onNeedRepeat: (questionId: string, reason: NeedRepeatReason) => void;
  onError: (e: EngineError) => void;
  /** 発話の PCM (16kHz mono)。R2 保存用で、両エンジン共通。 */
  onUtteranceAudio?: (audio: Float32Array) => void;
  /**
   * AI が読み上げた文。**realtime / live だけが持つ** — cascade は ttsPhrases.ts と
   * survey 定義から復元できるが、speech-to-speech は毎回生成なので残さないと復元できない
   * (spec D10: 設問文が改変された回を後から数えるため)。
   */
  onNarrated?: (text: string) => void;
  /** 費用の一次データ (spec §5.2 / 2026-09-14 L14)。形はエンジンごとに違う。 */
  onUsage?: (usage: Record<string, unknown>) => void;
  /**
   * 相手が黙ったまま時間が過ぎた (level は 0 から増える)。
   *
   * **realtime には無音の出口が構造的に無い** — `turn_detection.create_response` は
   * 「ユーザーが話し終えた後」にしか応答を作らないので、黙っていると永久に待つ
   * (2026-09-01 の実機ログで 61 秒の沈黙を実測)。cascade の SilenceTimer と
   * 同じ段数・同じ待ち時間で上げ、**促す文言も同じ TTS_PHRASES から取る**
   * (比較の軸を揃えるため)。
   */
  onSilence?: (level: number) => void;
  /** engine の状態が変わった。画面がマイクの表示に使う。 */
  onStateChange?: (s: EngineState) => void;
  /**
   * narrate() で頼んだ案内を読み終えた。2.1 は narration_done の tool で、live は
   * 「AI が話し終えた」で上げる (spec 2026-09-14 L10)。**来ないことがある**ので、
   * 画面は無音の出口も持つ (2.1 の §7.6)。
   */
  onNarrationDone?: () => void;
  /**
   * 重複・遅れて届いた tool を捨てた (live の二重の網、spec 2026-09-14 §3.5)。
   * 捨てた事実を R2 に残すためだけにある。
   */
  onToolIgnored?: (name: string, reason: "duplicate" | "stale") => void;
};

/**
 * start に渡す、このセッションの役割。**live はセッションの作成時に instructions と
 * backend が決まる** (起動後に変えられない) ので要る。2.1 は無視する。
 */
export type StartOptions = {
  purpose: "preflight" | "survey";
  /** 本編のとき、backend に渡す設問の一覧 */
  questions?: Question[];
};

export type ConversationEngine = {
  start: (opts?: StartOptions) => Promise<void>;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  /** いま伺う設問を差し替える。進行はアプリが持つ (半委任、D3)。 */
  setQuestion: (q: Question) => void;
  state: EngineState;
};

/**
 * speech-to-speech のエンジン (2.1 と live) が画面に見せる形 (spec 2026-09-14 L2)。
 * **画面を 2 つのエンジンで共有する**ための型で、step の操作をエンジンに持たせる —
 * 画面が instructions や tools を知らずに済み、比較の対象がエンジンの差に絞られる。
 */
export type SpeechEngine = ConversationEngine & {
  kind: "realtime" | "live";
  /** session.started に載せる (engine と、使ったモデル) */
  sessionMeta: Record<string, unknown>;
  /** 読み上げるだけの step (スピーカー確認・チュートリアル・完了案内) の文を読ませる */
  narrate: (lines: string[]) => void;
  /** 氏名を伺う (案内を読ませ、復唱して確認させる) */
  askName: () => void;
  /** 黙っている相手に促す (文言は TTS_PHRASES.silence0) */
  promptSilence: () => void;
  /** ユーザー操作の文脈で音を鳴らせる状態にする。マイク許可のタップから、最初の await より前に呼ぶ */
  unlock: () => void;
};

/** 画面が props で受けるエンジンの hook */
export type UseSpeechEngine = (cb: EngineCallbacks) => SpeechEngine;
